import { ChatTab } from "@/constants/chat";
import { defineStore } from "pinia";
import type { ThreadDto } from "@/api/chat/dto/thread.dto";
import type { CurrentDispatcher } from "@/api/dto/dispatcher.dto";
import type { UserDto } from "@/api/dto/user.dto";
import type { MessageDto } from "@/api/chat/dto/message.dto";
import { useAppStore } from "@/store/app.store";
import type { ChatService } from "@/services/socket/chat.service";
import dayjs from "dayjs";

const DRAFT_MESSAGES_STORAGE_KEY = "_draft_messages";
const CHAT_TABS_STORAGE_KEY = "_chat_tabs";

interface ChatState {
  chatService: ChatService | null;
  chatUser: any;
  threads: ChatStateThread[];
  threadsCount: ChatCounterState;
  threadsMessages: ChatCounterState;
  draftMessages: Record<string, any>;
  activeTabs: string[];
  offline: boolean;
  viewedThredId: number | null;
}

export interface ChatStateThread extends ThreadDto {
  counter: number;
  messages: MessageDto[];
  name: string;
  group: ChatTab;
  sorting?: boolean;
}

interface ChatCounterState {
  new: number;
  my: number;
  colleagues: number;
  archive: number;
}

const getThreadGroup = (dispatcher: CurrentDispatcher | undefined, thread: ThreadDto): ChatTab => {
  if (thread.resolved) {
    return ChatTab.Archive;
  }

  if (thread.dispatcherId === dispatcher?.id && !thread.resolved) {
    return ChatTab.My;
  }
  if (thread.dispatcherId && thread.dispatcherId !== dispatcher?.id) {
    return ChatTab.Colleagues;
  }
  if (!thread.dispatcherId) {
    return ChatTab.New;
  }

  return ChatTab.New;
};

const createChatStateThread = (thread: ThreadDto): ChatStateThread => {
  const appStore = useAppStore();
  return {
    ...thread,
    group: getThreadGroup(appStore.dispatcher, thread),
    counter: 0,
    messages: [],
    name: thread?.user?.name || "",
    lastMessage: thread.lastMessage || "",
  };
};

export const useChatStore = defineStore("chatStore", {
  state: (): ChatState => ({
    chatService: null,
    chatUser: {},
    threads: [],
    threadsCount: { new: 0, my: 0, colleagues: 0, archive: 0 },
    threadsMessages: { new: 0, my: 0, colleagues: 0, archive: 0 },
    draftMessages: {},
    activeTabs: [],
    offline: false,
    viewedThredId: null,
  }),
  actions: {
    initChat() {
      const storedMessages = localStorage.getItem(DRAFT_MESSAGES_STORAGE_KEY);
      if (storedMessages) {
        this.draftMessages = JSON.parse(storedMessages);
      }
      const storedTabs = localStorage.getItem(CHAT_TABS_STORAGE_KEY);
      if (storedTabs) {
        this.activeTabs = JSON.parse(storedTabs);
      }
    },
    setChatService(chatService: ChatService) {
      this.chatService = chatService;
    },
    setChatUser(user: UserDto) {
      this.chatUser = user;
    },
    setOffline(offline: boolean) {
      if (this.offline !== offline) {
        this.offline = offline;
      }
    },
    sortThreads() {
      const threads = [...this.threads];

      threads.sort((a, b) => {
        if (!a.lastMessageDt && !b.lastMessageDt) return 0;
        if (!a.lastMessageDt) return 1;
        if (!b.lastMessageDt) return -1;

        const dateA = dayjs(a.lastMessageDt);
        const dateB = dayjs(b.lastMessageDt);

        if (dateA.isBefore(dateB)) return 1;
        if (dateA.isAfter(dateB)) return -1;
        return 0;
      });

      this.threads = threads;
    },
    recalcThreadsCount() {
      const counts = { n: 0, m: 0, c: 0, a: 0 },
        newMessages = { n: 0, m: 0, c: 0, a: 0 };

      this.threads.forEach((thread) => {
        counts[thread.group]++;
        newMessages[thread.group] += thread.newMessagesCount > 0 ? 1 : 0;
      });

      // update threads count in state
      this.threadsCount.new = counts.n;
      this.threadsCount.my = counts.m;
      this.threadsCount.colleagues = counts.c;
      this.threadsMessages.new = newMessages.n;
      this.threadsMessages.my = newMessages.m;
      this.threadsMessages.colleagues = newMessages.c;
    },
    addThreads(newThreads: ThreadDto[]) {
      const appStore = useAppStore();

      this.threads = newThreads.map((thread) => {
        return {
          ...thread,
          counter: 0,
          messages: [],
          user: thread.user || {},
          name: thread.user.name,
          lastMessage: thread.lastMessage || "",
          group: getThreadGroup(appStore.dispatcher, thread),
        };
      });
    },
    upsertThreads(newThreads: ThreadDto[]) {
      const allThreads = [...this.threads];

      for (const thread of newThreads) {
        const stateThread = createChatStateThread(thread);
        allThreads.push(stateThread);
      }

      this.threads = allThreads;
    },
    upsertThread(thread: ThreadDto) {
      const stateThread: ChatStateThread = createChatStateThread(thread);

      const existsThread = this.threads.find((t) => t.id === stateThread.id);
      if (existsThread === undefined) {
        stateThread.counter = 0;
        stateThread.messages = [];
        stateThread.user = stateThread.user || {};
        stateThread.name = stateThread.user.name;
        stateThread.lastMessage = stateThread.lastMessage || "";

        this.threads.push({ ...stateThread });
      } else {
        existsThread.group = stateThread.group;
        existsThread.resolved = stateThread.resolved;
        existsThread.updatedDt = stateThread.updatedDt;
        existsThread.dispatcher = stateThread.dispatcher;
        existsThread.dispatcherId = stateThread.dispatcherId;
        existsThread.lastMessage = stateThread.lastMessage || "";
        existsThread.lastMessageDt = stateThread.lastMessageDt;
        existsThread.newMessagesCount = stateThread.newMessagesCount;
        existsThread.userWaitingSince = thread.userWaitingSince;
        if (stateThread.user) {
          existsThread.user = stateThread.user;
          existsThread.name = stateThread.user.name;
          existsThread.userId = stateThread.user.id;
        }
      }

      // sort threads when we explicitly denied it
      if (stateThread.sorting === undefined || stateThread.sorting) {
        this.sortThreads();
        this.recalcThreadsCount();
      }
    },
    updateThreadMessage(messageOrMessages: MessageDto | MessageDto[]) {
      const incomingMessages = Array.isArray(messageOrMessages) ? messageOrMessages : [messageOrMessages];

      if (!incomingMessages.length) {
        return;
      }

      const threadIndex = this.threads.findIndex((t) => t.id === incomingMessages[0].threadId);

      if (threadIndex === -1) {
        return;
      }

      const threadId = incomingMessages[0].threadId;

      if (threadId !== this.viewedThredId) {
        return;
      }

      const messages = this.threads[threadIndex].messages;

      for (const message of incomingMessages) {
        const existing = messages.find((m) => m.id === message.id);

        if (!existing) {
          messages.push(message);
        } else {
          existing.type = message.type;
          existing.text = message.text;
          existing.read = message.read;
          existing.deletedBy = message.deletedBy;
        }
      }

      messages.sort((a, b) => a.created.localeCompare(b.created));

      this.threads[threadIndex].counter++;
    },
    addOlderMessages(payload: { messages: MessageDto[]; threadId: number }) {
      const threadIndex = this.threads.findIndex((thread) => thread.id === payload.threadId);
      if (threadIndex === -1) return;
      const thread = this.threads[threadIndex];
      for (const message of payload.messages.reverse()) {
        if (thread.messages.findIndex((m) => m.id === message.id) === -1) {
          thread.messages.unshift(message);
        }
      }
    },
    saveDraftMessage(payload: { message: string; file: File | null; id: number }) {
      this.draftMessages[payload.id] = { message: payload.message, file: payload.file };
      localStorage.setItem(DRAFT_MESSAGES_STORAGE_KEY, JSON.stringify(this.draftMessages));
    },
    removeDraftMessage(id: number) {
      delete this.draftMessages[id];
      localStorage.setItem(DRAFT_MESSAGES_STORAGE_KEY, JSON.stringify(this.draftMessages));
    },
    saveTabsState(payload: string[]) {
      this.activeTabs = payload;
      localStorage.setItem(CHAT_TABS_STORAGE_KEY, JSON.stringify(this.activeTabs));
    },
    getThread(id: number) {
      const thread = this.threads.find((t) => t.id === id);
      return thread ? thread : null;
    },
    getDraftMessage(id: number): MessageDto | {} {
      let message = this.draftMessages[id];
      if (!message || typeof message !== "object") {
        message = {};
      }
      return message;
    },
    clearChat() {
      this.threads = [];
    },
    setViewedThreadId(id: number) {
      this.viewedThredId = id;
    },
  },
});
